import React, {
    ReactElement,
    useRef,
    useState,
    useEffect,
    useMemo,
    useCallback,
} from "react";
import styles from "./styles.module.scss";
import FilterButtonsList from "./filterButtonsList";
import { useStickyElement } from "@hooks/useStickyElement";
import FiltrationIcon from "./icons/filtrationIcon";
import XIcon from "./icons/xIcon";

export interface FilterOption {
    title: string;
    slug: string;
    icon?: string;
}

interface FilterSidebarProps {
    filters: FilterOption[];
    activeSlugs: string[];
    onToggle: (slug: string) => void;
    onReset: () => void;
    resetText?: string;
    title?: string;
}

export default function FilterSidebar({
    filters,
    activeSlugs,
    onToggle,
    onReset,
    resetText = "Reset Filters",
    title = "Filter by",
}: FilterSidebarProps): ReactElement {
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const activeCount = activeSlugs.length;

    const openSheet = useCallback(() => setIsSheetOpen(true), []);
    const closeSheet = useCallback(() => setIsSheetOpen(false), []);
    const handleReset = useCallback(() => {
        onReset();
    }, [onReset]);

    const activeFilters = useMemo(
        () => filters.filter((f) => activeSlugs.includes(f.slug)),
        [filters, activeSlugs],
    );

    useStickyElement(containerRef, {
        minWidth: 1024,
        getBoundary: () => wrapperRef.current?.parentElement ?? null,
        wrapperRef,
    });

    // Close sheet on backdrop click / escape key
    useEffect(() => {
        if (!isSheetOpen) return;

        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeSheet();
        };
        const originalOverflow = document.body.style.overflow;

        window.addEventListener("keydown", handleKey);
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", handleKey);
            document.body.style.overflow = originalOverflow;
        };
    }, [isSheetOpen, closeSheet]);

    if (!filters.length) return <></>;

    return (
        <>
            {/* ── Desktop Sidebar ── */}
            {/* Outer spacer stays in flex flow; inner containerRef goes fixed/absolute */}
            <div ref={wrapperRef} className={styles.filterSidebarOuter}>
                <div ref={containerRef} className={styles.filterSidebarWrapper}>
                    <div className={styles.filterHeader}>
                        <div className={styles.filterTitleWrapper}>
                            <span className={styles.filterTitle}>{title}</span>
                            {/* <span className={styles.filterCount}>
                                {activeCount}
                            </span> */}
                        </div>
                        {activeCount > 0 && (
                            <button
                                type="button"
                                className={styles.resetButton}
                                onClick={handleReset}
                            >
                                {resetText}
                            </button>
                        )}
                    </div>

                    <FilterButtonsList
                        filters={filters}
                        activeSlugs={activeSlugs}
                        onToggle={onToggle}
                    />
                </div>
            </div>
            {/* ── Mobile ── */}
            <div className={styles.mobileControls}>
                <button
                    type="button"
                    className={styles.filtrationIcon}
                    onClick={openSheet}
                    aria-expanded={isSheetOpen}
                >
                    <FiltrationIcon />
                    {activeCount > 0 && (
                        <span className={styles.filtrationIconBadge}>
                            {activeCount}
                        </span>
                    )}
                </button>

                {activeFilters.length > 0 && (
                    <div className={styles.activeBadges}>
                        {activeFilters.map((f) => (
                            <span key={f.slug} className={styles.badge}>
                                <span className={styles.badgeTitle}>
                                    {f.title}
                                </span>
                                <button
                                    type="button"
                                    className={styles.badgeRemove}
                                    onClick={() => onToggle(f.slug)}
                                >
                                    <XIcon />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Mobile: Bottom Sheet ── */}
            {isSheetOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className={styles.backdrop}
                        onClick={closeSheet}
                        aria-hidden="true"
                    />

                    {/* Sheet */}
                    <div
                        className={styles.bottomSheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Filters"
                    >
                        {/* Drag handle */}
                        <div className={styles.sheetHandle} />
                        <div className={styles.sheetContentWrapper}>
                            <div className={styles.sheetHeader}>
                                <div className={styles.filterTitleWrapper}>
                                    <span className={styles.filterTitle}>
                                        {title}
                                    </span>
                                    {/* {activeCount > 0 && (
                                        <span className={styles.filterCount}>
                                            {activeCount}
                                        </span>
                                    )} */}
                                </div>
                                {activeCount > 0 && (
                                    <button
                                        type="button"
                                        className={styles.resetButton}
                                        onClick={handleReset}
                                    >
                                        {resetText}
                                    </button>
                                )}
                            </div>

                            <div className={styles.sheetContent}>
                                <FilterButtonsList
                                    filters={filters}
                                    activeSlugs={activeSlugs}
                                    onToggle={onToggle}
                                />
                            </div>

                            <div className={styles.sheetFooter}>
                                <button
                                    type="button"
                                    className={styles.sheetClose}
                                    onClick={closeSheet}
                                >
                                    <span>
                                        <XIcon />
                                    </span>
                                    <span className={styles.sheetCloseText}>
                                        Close
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
